/**
 * feat-298 — the header toolbar under the page subtitle (design NOTES §9).
 *
 * Expand all / Collapse all on the left. On the right, an EMPTY, named slot for
 * feat-#5's "View as location / role" control (spec §3, design Q1). It renders
 * nothing in feat-298 — the slot is the layout, not a placeholder.
 */
import React from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { FOCUS_RING } from './Collapsible';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';

export const TOOLBAR_BTN =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-sm font-medium ' +
  `text-ink ring-1 ring-black/10 hover:bg-cream-dark ${FOCUS_RING}`;

export const ReviewToolbar: React.FC<{
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** feat-300 — rendered after the Sections group, left of the scope slot (the
   *  AI-edit "N need you" counter). Additive: absent ⇒ today's toolbar. */
  children?: React.ReactNode;
  /** feat-302 — the View as pickers, rendered INTO the reserved scope slot.
   *  Absent ⇒ the slot stays empty, exactly as feat-298 shipped it. */
  scopeControls?: React.ReactNode;
  scopeLabel?: string;
}> = ({ onExpandAll, onCollapseAll, children, scopeControls, scopeLabel }) => (
  <div className="mt-4 flex flex-wrap items-center gap-2">
    <div className="flex items-center gap-2" role="group" aria-label={COPY.sectionsGroupLabel}>
      <button type="button" className={TOOLBAR_BTN} onClick={onExpandAll}>
        <ChevronsUpDown className="w-3.5 h-3.5" aria-hidden />
        {COPY.expandAll}
      </button>
      <button type="button" className={TOOLBAR_BTN} onClick={onCollapseAll}>
        <ChevronsDownUp className="w-3.5 h-3.5" aria-hidden />
        {COPY.collapseAll}
      </button>
    </div>
    {children}
    {scopeControls ? (
      // Below `sm` the group takes the full row under Expand/Collapse (NOTES §2).
      <div
        className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2"
        data-slot="review-scope-controls"
        role="group"
        aria-label={scopeLabel}
      >
        {scopeControls}
      </div>
    ) : (
      <div className="ml-auto flex items-center gap-2" data-slot="review-scope-controls" />
    )}
  </div>
);
